import React from 'react';
import Recorder from 'opus-recorder';

/**
 * Recording a voice note in the format WhatsApp wants: ogg/opus, 16 kHz mono.
 *
 * Ported from the legacy QR-session chat, which already shipped the encoder
 * worker under `public/workers/`. The hook owns the microphone stream, the
 * timer and the live level meter, and hands back a `File` on stop — or nothing
 * on cancel. Everything is torn down on unmount so a navigated-away recording
 * does not keep the microphone light on.
 */
export default function useVoiceRecorder() {
	const [recording, setRecording] = React.useState(false);
	const [seconds, setSeconds] = React.useState(0);
	const [level, setLevel] = React.useState(0);
	const [error, setError] = React.useState(null);

	const recorderRef = React.useRef(null);
	const streamRef = React.useRef(null);
	const timerRef = React.useRef(null);
	const rafRef = React.useRef(null);
	const contextRef = React.useRef(null);
	const resultRef = React.useRef(null); // { resolve }
	const cancelledRef = React.useRef(false);

	const teardown = React.useCallback(() => {
		clearInterval(timerRef.current);
		timerRef.current = null;
		if (rafRef.current) cancelAnimationFrame(rafRef.current);
		rafRef.current = null;
		streamRef.current?.getTracks().forEach((t) => t.stop());
		streamRef.current = null;
		contextRef.current?.close().catch(() => {});
		contextRef.current = null;
		setRecording(false);
		setLevel(0);
	}, []);

	React.useEffect(
		() => () => {
			cancelledRef.current = true;
			try {
				recorderRef.current?.stop();
			} catch (_) {
				// nothing to stop
			}
			teardown();
		},
		[teardown],
	);

	const start = React.useCallback(async () => {
		setError(null);
		cancelledRef.current = false;
		setSeconds(0);
		let stream;
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		} catch (err) {
			setError('Microphone access was refused.');
			return false;
		}
		streamRef.current = stream;

		const recorder = new Recorder({
			encoderPath: '/workers/encoderWorker.min.js',
			encoderSampleRate: 16000,
			outputContainer: 'ogg',
			encoderBitRate: 16000,
			numberOfChannels: 1,
			resampleQuality: 3,
		});
		recorderRef.current = recorder;

		recorder.ondataavailable = (typedArray) => {
			const blob = new Blob([typedArray], { type: 'audio/ogg; codecs=opus' });
			const file = new File([blob], `voice-${Date.now()}.ogg`, { type: 'audio/ogg; codecs=opus' });
			resultRef.current?.resolve(cancelledRef.current ? null : file);
			resultRef.current = null;
		};
		recorder.onerror = () => {
			setError('Recording failed.');
			resultRef.current?.resolve(null);
			resultRef.current = null;
			teardown();
		};

		// Live level for the little waveform: a coarse analyser is plenty.
		try {
			const Ctx = window.AudioContext || window.webkitAudioContext;
			const ctx = new Ctx();
			contextRef.current = ctx;
			const analyser = ctx.createAnalyser();
			analyser.fftSize = 32;
			ctx.createMediaStreamSource(stream).connect(analyser);
			const data = new Uint8Array(analyser.frequencyBinCount);
			const tick = () => {
				analyser.getByteFrequencyData(data);
				let sum = 0;
				for (let i = 0; i < data.length; i += 1) sum += data[i];
				setLevel(Math.min(1, sum / data.length / 50));
				rafRef.current = requestAnimationFrame(tick);
			};
			rafRef.current = requestAnimationFrame(tick);
		} catch (_) {
			// No analyser: the timer still runs, the bars just stay flat.
		}

		try {
			await recorder.start();
		} catch (err) {
			setError('Recording could not start.');
			teardown();
			return false;
		}
		setRecording(true);
		timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
		return true;
	}, [teardown]);

	/** Stop and get the file (null if cancelled or empty). */
	const stop = React.useCallback(async () => {
		const recorder = recorderRef.current;
		if (!recorder) return null;
		const done = new Promise((resolve) => {
			resultRef.current = { resolve };
		});
		try {
			await recorder.stop();
		} catch (_) {
			resultRef.current?.resolve(null);
			resultRef.current = null;
		}
		teardown();
		recorderRef.current = null;
		const file = await done;
		return file && file.size > 0 ? file : null;
	}, [teardown]);

	const cancel = React.useCallback(async () => {
		cancelledRef.current = true;
		await stop();
		setSeconds(0);
	}, [stop]);

	return { recording, seconds, level, error, start, stop, cancel };
}
